import { API_BASE_URL, ApiError, apiRequest, getToken } from "./apiClient";
import { TOP_MATCH_LIMIT } from "../constants/recruiterAi";
import type {
  AdminJob,
  AdminStats,
  AdminSubmission,
  AdminPeopleItem,
  AffiliateCandidate,
  AffiliateProfile,
  AffiliateStats,
  AuditLogEntry,
  Candidate,
  Company,
  CompanyAccountManager,
  CompanyCreatePayload,
  CompanyMembership,
  CompanyType,
  CompanyUser,
  ConsultancyRateCard,
  Dispute,
  DisputeType,
  EarningsSummary,
  EmailCampaign,
  EmailCampaignDetail,
  EmailCampaignTemplate,
  FlowTemplate,
  IntakeLink,
  IntakePublicInfo,
  IntakePhoneOtpResponse,
  IntakePhoneVerifiedResponse,
  IntakeResumeParseResponse,
  IntakeResumeUploadResponse,
  IntakeSubmitPayload,
  IntakeSubmitResponse,
  Job,
  JobCreatePayload,
  JobDetail,
  JobParseDocumentResponse,
  JobStatus,
  KycStatus,
  AdminInvoice,
  AdminInvoiceListItem,
  AdminPayout,
  UninvoicedPlacement,
  Page,
  Payout,
  RecruiterKycStatus,
  RecruiterProfile,
  AdminRecruiterKyc,
  AdminAffiliateKyc,
  AdminCompanyKyc,
  CompanyKycStatus,
  HrKyc,
  Segment,
  SegmentCatalog,
  SkillCatalog,
  AdminUnmappedSkill,
  CityCatalog,
  Stage,
  StageInput,
  Submission,
  SubmissionDetail,
  SubmissionStatus,
  User,
  UserProfile,
  VerificationStatus,
  NotificationPreferences,
  DigestRunResult,
  CandidateProfile,
  TimelineEntry,
  DashboardCatalog,
  DashboardLayout,
  DashboardReportRun,
  CompanyContract,
  ContractSummary,
  InterviewPrep,
  JobInsight,
  JobInsightPublic,
  JobInsightType,
  TopJobMatch,
  PoolCandidateMatch,
  ContactLog,
  RoleFamilyCatalog,
  SubmissionAttestation,
} from "../types/api";

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  user: User;
  account_deletion_recovered: boolean;
  message?: string | null;
}

export interface GoogleAuthResponse {
  access_token?: string | null;
  refresh_token?: string | null;
  expires_in?: number | null;
  token_type?: string;
  user?: User | null;
  needs_role_selection?: boolean;
  google_signup_token?: string | null;
  apple_signup_token?: string | null;
  status?: string | null;
  message?: string | null;
  account_deletion_recovered?: boolean;
}

export interface PublicAuthConfig {
  google_client_id: string | null;
  google_enabled: boolean;
  apple_client_id: string | null;
  apple_enabled: boolean;
  apple_redirect_uri: string | null;
  portal_url: string | null;
}

export const authApi = {
  publicConfig: () =>
    apiRequest<PublicAuthConfig>("/auth/public-config", { auth: false }),

  login: (email: string, password: string) =>
    apiRequest<TokenResponse>("/auth/login", { method: "POST", body: { email, password }, auth: false }),

  register: (payload: {
    email: string;
    password: string;
    full_name: string;
    phone?: string;
    city?: string;
    segments?: Segment[];
    years_experience?: number;
    bio?: string;
    resume_id?: string;
    terms_version: string;
    terms_accepted: boolean;
  }) => apiRequest<TokenResponse>("/auth/register", { method: "POST", body: payload, auth: false }),

  registerAffiliate: (payload: {
    email: string;
    password: string;
    full_name: string;
    phone: string | null;
    city?: string;
    terms_version: string;
    terms_accepted: boolean;
  }) =>
    apiRequest<TokenResponse>("/auth/register-affiliate", {
      method: "POST",
      body: payload,
      auth: false,
    }),

  registerCompany: (payload: {
    email: string;
    password: string;
    full_name: string;
    phone: string;
    company_type: CompanyType;
    legal_name: string;
    display_name?: string;
    gstin?: string;
    website?: string;
    industry?: string;
    city?: string;
    about?: string;
    segments?: Segment[];
    terms_version: string;
    terms_accepted: boolean;
  }) =>
    apiRequest<TokenResponse>("/auth/register-company", {
      method: "POST",
      body: payload,
      auth: false,
    }),

  google: (payload: { id_token: string }) =>
    apiRequest<GoogleAuthResponse>("/auth/google", { method: "POST", body: payload, auth: false }),

  apple: (payload: { id_token: string; authorization_code?: string; full_name?: string }) =>
    apiRequest<GoogleAuthResponse>("/auth/apple", { method: "POST", body: payload, auth: false }),

  googleComplete: (payload: {
    google_signup_token: string;
    requested_role: "recruiter" | "company_user";
    company_name?: string;
    create_company?: boolean;
    company_type?: CompanyType;
    legal_name?: string;
    display_name?: string;
    gstin?: string;
    website?: string;
    industry?: string;
    segments?: Segment[];
    city?: string;
    years_experience?: number;
    bio?: string;
    resume_id?: string;
    terms_version: string;
    terms_accepted: boolean;
  }) => apiRequest<GoogleAuthResponse>("/auth/google/complete", { method: "POST", body: payload, auth: false }),

  appleComplete: (payload: {
    apple_signup_token: string;
    requested_role: "recruiter" | "company_user";
    company_name?: string;
    create_company?: boolean;
    company_type?: CompanyType;
    legal_name?: string;
    display_name?: string;
    gstin?: string;
    website?: string;
    industry?: string;
    segments?: Segment[];
    city?: string;
    years_experience?: number;
    bio?: string;
    resume_id?: string;
    terms_version: string;
    terms_accepted: boolean;
  }) => apiRequest<GoogleAuthResponse>("/auth/apple/complete", { method: "POST", body: payload, auth: false }),

  joinTerms: (role: "recruiter" | "company_user" | "affiliate" | "client" | "consultancy") =>
    apiRequest<{ role: string; version: string }>("/auth/join-terms", { query: { role }, auth: false }),

  forgotPassword: (email: string) =>
    apiRequest<void>("/auth/forgot-password", { method: "POST", body: { email }, auth: false }),

  resetPassword: (token: string, password: string) =>
    apiRequest<void>("/auth/reset-password", { method: "POST", body: { token, password }, auth: false }),

  logout: (refreshToken: string, allDevices = false) =>
    apiRequest<void>("/auth/logout", {
      method: "POST",
      body: { refresh_token: refreshToken, all_devices: allDevices },
      auth: false,
    }),

  me: () => apiRequest<User>("/auth/me"),
};

export const recruiterApi = {
  myProfile: () => apiRequest<RecruiterProfile>("/users/me/recruiter-profile"),

  updateMyProfile: (payload: Partial<Pick<RecruiterProfile, "city" | "years_experience" | "bio">>) =>
    apiRequest<RecruiterProfile>("/users/me/recruiter-profile", { method: "PATCH", body: payload }),

  kycStatus: () => apiRequest<RecruiterKycStatus>("/users/me/kyc"),

  patchKycIdentity: (payload: { aadhaar_number?: string; pan?: string }) =>
    apiRequest<RecruiterKycStatus>("/users/me/kyc", { method: "PATCH", body: payload }),

  uploadKycFile: async (slot: string, file: File) => {
    const body = new FormData();
    body.set("file", file);
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE_URL}/users/me/kyc/files/${slot}`, {
      method: "POST",
      headers,
      body,
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;
    if (!response.ok) {
      throw new ApiError(response.status, data?.error?.code ?? "upload_failed", data?.error?.message || "Upload failed");
    }
    return data as RecruiterKycStatus;
  },

  submitKyc: (payload: {
    aadhaar_number?: string;
    pan?: string;
    resume_id?: string;
    account_holder_name?: string;
    account_number?: string;
    ifsc?: string;
    upi_id?: string;
  }) => apiRequest<RecruiterKycStatus>("/users/me/kyc/submit", { method: "POST", body: payload }),
};

export const affiliateApi = {
  myProfile: () => apiRequest<AffiliateProfile>("/users/me/affiliate-profile"),

  updateMyProfile: (payload: Partial<Pick<AffiliateProfile, "city">>) =>
    apiRequest<AffiliateProfile>("/users/me/affiliate-profile", { method: "PATCH", body: payload }),

  createLink: () => apiRequest<IntakeLink>("/users/me/intake-link", { method: "POST" }),

  listLinks: () => apiRequest<IntakeLink[]>("/users/me/intake-links"),

  deactivate: (id: string) =>
    apiRequest<IntakeLink>(`/users/intake-links/${id}/deactivate`, { method: "POST" }),

  stats: () => apiRequest<AffiliateStats>("/users/me/affiliate-stats"),

  listCandidates: (params: { page?: number; page_size?: number } = {}) =>
    apiRequest<Page<AffiliateCandidate>>("/users/me/affiliate-candidates", { query: params }),
};

export const usersApi = {
  updateMe: (payload: { full_name?: string; phone?: string }) =>
    apiRequest<User>("/users/me", { method: "PATCH", body: payload }),

  requestDeletion: () =>
    apiRequest<void>("/users/me/deletion-request", { method: "POST" }),

  requestEmailOtp: () =>
    apiRequest<{ message: string; email?: string }>("/users/me/email/request-otp", {
      method: "POST",
    }),

  verifyEmailOtp: (code: string) =>
    apiRequest<User>("/users/me/email/verify-otp", { method: "POST", body: { code } }),

  requestPhoneOtp: () =>
    apiRequest<{ message: string; phone?: string }>("/users/me/phone/request-otp", {
      method: "POST",
    }),

  verifyPhoneOtp: (code: string) =>
    apiRequest<User>("/users/me/phone/verify-otp", { method: "POST", body: { code } }),

  getNotificationPreferences: () =>
    apiRequest<NotificationPreferences>("/users/me/notification-preferences"),

  patchNotificationPreferences: (payload: {
    mode: "all" | "summary";
    summary_cadence?: "daily" | "weekly" | null;
  }) =>
    apiRequest<NotificationPreferences>("/users/me/notification-preferences", {
      method: "PATCH",
      body: payload,
    }),
};

export const companyApi = {
  myCompany: () => apiRequest<CompanyMembership>("/users/me/company"),

  get: (id: string) => apiRequest<Company>(`/companies/${id}`),

  listUsers: (companyId: string) =>
    apiRequest<CompanyUser[]>(`/companies/${companyId}/users`),

  addUser: (
    companyId: string,
    payload: {
      email: string;
      password: string;
      full_name: string;
      phone?: string;
      company_role?: string;
    },
  ) =>
    apiRequest<CompanyUser>(`/companies/${companyId}/users`, {
      method: "POST",
      body: payload,
    }),

  kycStatus: () => apiRequest<CompanyKycStatus>("/companies/me/kyc"),

  patchKyc: (payload: {
    gstin?: string;
    registration_no?: string;
    company_pan?: string;
    authorized_signatory_name?: string;
    authorized_signatory_pan?: string;
    billing_address?: Record<string, unknown>;
  }) => apiRequest<CompanyKycStatus>("/companies/me/kyc", { method: "PATCH", body: payload }),

  uploadKycFile: async (slot: string, file: File) => {
    const body = new FormData();
    body.set("file", file);
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE_URL}/companies/me/kyc/files/${slot}`, {
      method: "POST",
      headers,
      body,
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;
    if (!response.ok) {
      throw new ApiError(response.status, data?.error?.code ?? "upload_failed", data?.error?.message || "Upload failed");
    }
    return data as CompanyKycStatus;
  },

  submitKyc: (payload: {
    gstin?: string;
    registration_no?: string;
    company_pan?: string;
    authorized_signatory_name?: string;
    authorized_signatory_pan?: string;
    billing_address?: Record<string, unknown>;
  }) => apiRequest<CompanyKycStatus>("/companies/me/kyc/submit", { method: "POST", body: payload }),

  hrKyc: () => apiRequest<HrKyc>("/users/me/hr-kyc"),

  patchHrKyc: (payload: { designation?: string; personal_pan?: string }) =>
    apiRequest<HrKyc>("/users/me/hr-kyc", { method: "PATCH", body: payload }),

  submitHrKyc: (payload: {
    designation?: string;
    personal_pan?: string;
    conflict_of_interest_accepted: boolean;
  }) => apiRequest<HrKyc>("/users/me/hr-kyc/submit", { method: "POST", body: payload }),
};

export const consultancyApi = {
  listRecruiters: () => apiRequest<RecruiterProfile[]>("/consultancy/recruiters"),

  inviteRecruiter: (payload: {
    email: string;
    password: string;
    full_name: string;
    phone?: string;
    city?: string;
    segments?: Segment[];
  }) =>
    apiRequest<RecruiterProfile>("/consultancy/recruiters/invite", {
      method: "POST",
      body: payload,
    }),

  rateCard: () => apiRequest<ConsultancyRateCard | null>("/consultancy/rate-card"),

  listJobs: (params: { segment?: Segment; city?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Job>>("/consultancy/jobs", { query: params }),

  listCandidates: (params: { q?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Candidate>>("/consultancy/candidates", { query: params }),

  listSubmissions: (params: { status?: SubmissionStatus; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Submission>>("/consultancy/submissions", { query: params }),

  earnings: () => apiRequest<EarningsSummary>("/consultancy/earnings"),

  payouts: (params: { status?: "pending" | "received" | "all"; submission_id?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Payout>>("/consultancy/payouts", { query: params }),
};

export const adminApi = {
  stats: () => apiRequest<AdminStats>("/admin/stats"),

  listCampaignCandidates: (params: {
    q?: string;
    page?: number;
    page_size?: number;
    sort?: string;
    match_tier?: string;
    contact?: string;
  } = {}) => apiRequest<Page<Candidate>>("/admin/candidates", { query: params }),

  listRecruiters: (params: { kyc_status?: KycStatus; docs_missing?: boolean; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<RecruiterProfile>>("/admin/recruiters", { query: params }),

  getRecruiter: (id: string) => apiRequest<RecruiterProfile>(`/admin/recruiters/${id}`),

  getRecruiterKyc: (id: string) => apiRequest<AdminRecruiterKyc>(`/admin/recruiters/${id}/kyc`),

  fetchKycFile: async (id: string, slot: string) => {
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE_URL}/admin/recruiters/${id}/kyc-files/${slot}`, { headers });
    if (!response.ok) {
      const text = await response.text();
      let data: { error?: { code?: string; message?: string } } | undefined;
      try {
        data = text ? JSON.parse(text) : undefined;
      } catch {
        data = undefined;
      }
      throw new ApiError(
        response.status,
        data?.error?.code ?? "download_failed",
        data?.error?.message || "Could not load KYC file",
      );
    }
    return response.blob();
  },

  setRecruiterKyc: (
    id: string,
    payload: { kyc_status: KycStatus; notes?: string; segments?: Segment[] },
  ) => apiRequest<RecruiterProfile>(`/admin/recruiters/${id}/kyc`, { method: "POST", body: payload }),

  getAffiliate: (id: string) => apiRequest<AffiliateProfile>(`/admin/affiliates/${id}`),

  getAffiliateKyc: (id: string) => apiRequest<AdminAffiliateKyc>(`/admin/affiliates/${id}/kyc`),

  fetchAffiliateKycFile: async (id: string, slot: string) => {
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE_URL}/admin/affiliates/${id}/kyc-files/${slot}`, { headers });
    if (!response.ok) {
      const text = await response.text();
      let data: { error?: { code?: string; message?: string } } | undefined;
      try {
        data = text ? JSON.parse(text) : undefined;
      } catch {
        data = undefined;
      }
      throw new ApiError(
        response.status,
        data?.error?.code ?? "download_failed",
        data?.error?.message || "Could not load KYC file",
      );
    }
    return response.blob();
  },

  setAffiliateKyc: (
    id: string,
    payload: { kyc_status: KycStatus; notes?: string },
  ) => apiRequest<AdminAffiliateKyc>(`/admin/affiliates/${id}/kyc`, { method: "POST", body: payload }),

  getCompanyKyc: (id: string) => apiRequest<AdminCompanyKyc>(`/admin/companies/${id}/kyc`),

  fetchCompanyKycFile: async (id: string, slot: string) => {
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE_URL}/admin/companies/${id}/kyc-files/${slot}`, { headers });
    if (!response.ok) {
      const text = await response.text();
      let data: { error?: { code?: string; message?: string } } | undefined;
      try {
        data = text ? JSON.parse(text) : undefined;
      } catch {
        data = undefined;
      }
      throw new ApiError(
        response.status,
        data?.error?.code ?? "download_failed",
        data?.error?.message || "Could not load KYC file",
      );
    }
    return response.blob();
  },

  setCompanyUserKyc: (
    membershipId: string,
    payload: { kyc_status?: KycStatus; kyc_bypass?: boolean; notes?: string },
  ) => apiRequest<HrKyc>(`/admin/company-users/${membershipId}/kyc`, { method: "POST", body: payload }),

  listUsers: (params: { role?: User["role"]; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<User>>("/admin/users", { query: params }),

  listCompanies: (params: { verification_status?: VerificationStatus; company_type?: CompanyType; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Company>>("/admin/companies", { query: params }),

  verifyCompany: (id: string, payload: { status: VerificationStatus; notes?: string }) =>
    apiRequest<Company>(`/admin/companies/${id}/verify`, { method: "POST", body: payload }),

  createCompany: (payload: CompanyCreatePayload) =>
    apiRequest<Company>("/admin/companies", { method: "POST", body: payload }),

  createRecruiter: (payload: {
    email: string;
    password: string;
    full_name: string;
    phone?: string;
    city?: string;
    segments?: Segment[];
    kyc_status?: KycStatus;
  }) => apiRequest<RecruiterProfile>("/admin/recruiters", { method: "POST", body: payload }),

  addCompanyUser: (
    companyId: string,
    payload: {
      email: string;
      password: string;
      full_name: string;
      phone?: string;
      company_role?: string;
    },
  ) => apiRequest<unknown>(`/admin/companies/${companyId}/users`, { method: "POST", body: payload }),

  listAccountManagers: (companyId: string) =>
    apiRequest<CompanyAccountManager[]>(`/admin/companies/${companyId}/account-managers`),

  addAccountManager: (
    companyId: string,
    payload: { admin_user_id: string; is_primary?: boolean },
  ) =>
    apiRequest<CompanyAccountManager>(`/admin/companies/${companyId}/account-managers`, {
      method: "POST",
      body: payload,
    }),

  removeAccountManager: (companyId: string, linkId: string) =>
    apiRequest<void>(`/admin/companies/${companyId}/account-managers/${linkId}`, {
      method: "DELETE",
    }),

  listConsultancyRateCards: (companyId: string) =>
    apiRequest<ConsultancyRateCard[]>(`/admin/companies/${companyId}/rate-cards`),

  createConsultancyRateCard: (
    companyId: string,
    payload: {
      name?: string;
      recruiter_fee_pct: number;
      sourcing_fee_pct: number;
      platform_fee_pct: number;
      effective_from: string;
      effective_to?: string | null;
      notes?: string;
      is_default?: boolean;
      status?: string;
    },
  ) =>
    apiRequest<ConsultancyRateCard>(`/admin/companies/${companyId}/rate-cards`, {
      method: "POST",
      body: payload,
    }),

  updateConsultancyRateCard: (
    cardId: string,
    payload: Partial<{
      name: string;
      recruiter_fee_pct: number;
      sourcing_fee_pct: number;
      platform_fee_pct: number;
      effective_from: string;
      effective_to: string | null;
      notes: string;
      is_default: boolean;
      status: string;
    }>,
  ) => apiRequest<ConsultancyRateCard>(`/admin/rate-cards/${cardId}`, { method: "PUT", body: payload }),

  listAuditLogs: (params: {
    page?: number;
    page_size?: number;
    actor_user_id?: string;
    entity_type?: string;
    entity_id?: string;
    action?: string;
    from?: string;
    to?: string;
  } = {}) =>
    apiRequest<Page<AuditLogEntry>>("/admin/audit-logs", { query: params }),
  listPeople: (params: {
    q?: string;
    role?: User["role"];
    status?: string;
    company_type?: CompanyType;
    page?: number;
    page_size?: number;
  } = {}) => apiRequest<Page<AdminPeopleItem>>("/admin/people", { query: params }),

  deactivateUser: (userId: string, reason: string) =>
    apiRequest<User>(`/admin/users/${userId}/deactivate`, { method: "POST", body: { reason } }),

  reactivateUser: (userId: string, reason: string) =>
    apiRequest<User>(`/admin/users/${userId}/reactivate`, { method: "POST", body: { reason } }),

  listSubmissions: (params: { status?: SubmissionStatus; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<AdminSubmission>>("/admin/submissions", { query: params }),

  updateCompanyFeedback: (
    submissionId: string,
    payload: { company_feedback?: string | null; company_rating?: number | null },
  ) =>
    apiRequest<AdminSubmission>(`/admin/submissions/${submissionId}/company-feedback`, {
      method: "PATCH",
      body: payload,
    }),

  listJobInsights: (jobId: string) => apiRequest<JobInsight[]>(`/admin/jobs/${jobId}/insights`),

  createJobInsight: (
    jobId: string,
    payload: {
      insight_type: JobInsightType;
      content: string;
      internal_note?: string;
      published?: boolean;
    },
  ) =>
    apiRequest<JobInsight>(`/admin/jobs/${jobId}/insights`, {
      method: "POST",
      body: payload,
    }),

  generateJobInsight: (jobId: string) =>
    apiRequest<JobInsight>(`/admin/jobs/${jobId}/insights/generate`, { method: "POST" }),

  updateJobInsight: (
    jobId: string,
    insightId: string,
    payload: Partial<{
      insight_type: JobInsightType;
      content: string;
      internal_note: string | null;
      published: boolean;
    }>,
  ) =>
    apiRequest<JobInsight>(`/admin/jobs/${jobId}/insights/${insightId}`, {
      method: "PATCH",
      body: payload,
    }),

  deleteJobInsight: (jobId: string, insightId: string) =>
    apiRequest<{ message: string }>(`/admin/jobs/${jobId}/insights/${insightId}`, {
      method: "DELETE",
    }),

  listJobs: (params: { status?: JobStatus; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<AdminJob>>("/admin/jobs", { query: params }),

  getJob: (jobId: string) => apiRequest<AdminJob>(`/admin/jobs/${jobId}`),

  approveJobFees: (
    jobId: string,
    payload: {
      sourcing_fee_pct?: number;
      recruiter_fee_pct?: number;
      platform_fee_pct?: number;
      company_contract_id?: string;
      consultancy_sourcing_fee_pct?: number;
      consultancy_recruiter_fee_pct?: number;
      consultancy_platform_fee_pct?: number;
      redacted_description?: string | null;
      redacted_requirements?: string | null;
    } = {},
  ) =>
    apiRequest<AdminJob>(`/admin/jobs/${jobId}/approve-fees`, {
      method: "POST",
      body: payload,
    }),

  backfillAllOpenJobs: () =>
    apiRequest<{ jobs_scanned: number; matches_created: number }>("/admin/matching/backfill", {
      method: "POST",
    }),

  backfillJob: (jobId: string) =>
    apiRequest<{ created: number }>(`/admin/jobs/${jobId}/backfill`, { method: "POST" }),

  downloadCandidateImportTemplate: (format: "xlsx" | "csv" = "xlsx") =>
    downloadFile(
      `/admin/queue/import/template.${format}`,
      format === "xlsx" ? "axiohire-explore-candidates.xlsx" : "axiohire-explore-candidates.csv",
    ),

  importCandidates: (file: File, payload: { job_id?: string; consent_basis: string }) =>
    uploadFileRequest<{
      id: string;
      job_id: string | null;
      filename: string;
      status: string;
      row_count: number;
      success_count: number;
      error_count: number;
      errors: { row: number; error: string }[];
      consent_basis: string | null;
    }>("/admin/queue/import", payload, file),

  listSkills: (params: { q?: string; category?: string } = {}) =>
    apiRequest<
      { code: string; label: string; category: string; sort_order: number; is_active: boolean }[]
    >("/admin/skills", { query: params }),

  listUnmappedSkills: (params: { limit?: number } = {}) =>
    apiRequest<AdminUnmappedSkill[]>("/admin/skills/unmapped", { query: params }),

  suggestUnmappedSkill: (alias: string) =>
    apiRequest<AdminUnmappedSkill>(
      `/admin/skills/unmapped/${encodeURIComponent(alias)}/suggest`,
      { method: "POST" },
    ),

  createSkill: (payload: {
    label: string;
    category: string;
    code?: string;
    description?: string;
    aliases?: string[];
    map_alias?: string;
  }) =>
    apiRequest<{ code: string; label: string; category: string; message: string }>("/admin/skills", {
      method: "POST",
      body: payload,
    }),

  mapSkillAlias: (payload: { alias: string; skill_code: string; apply_to_candidates?: boolean }) =>
    apiRequest<{ alias: string; skill_code: string; message: string }>("/admin/skills/aliases", {
      method: "POST",
      body: payload,
    }),

  dismissUnmappedSkill: (alias: string) =>
    apiRequest<{ message: string }>(`/admin/skills/unmapped/${encodeURIComponent(alias)}`, {
      method: "DELETE",
    }),

  listDisputes: (params: { status?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Dispute>>("/admin/disputes", { query: params }),

  investigateDispute: (id: string, payload: { internal_notes?: string } = {}) =>
    apiRequest<Dispute>(`/admin/disputes/${id}/investigate`, { method: "POST", body: payload }),

  resolveDispute: (id: string, payload: { resolution_notes: string; internal_notes?: string }) =>
    apiRequest<Dispute>(`/admin/disputes/${id}/resolve`, { method: "POST", body: payload }),

  rejectDispute: (id: string, payload: { resolution_notes: string; internal_notes?: string }) =>
    apiRequest<Dispute>(`/admin/disputes/${id}/reject`, { method: "POST", body: payload }),

  runDigest: (cadence: "daily" | "weekly" = "daily") =>
    apiRequest<DigestRunResult>("/admin/notifications/run-digest", {
      method: "POST",
      query: { cadence },
    }),

  listCompanyContracts: (companyId: string) =>
    apiRequest<CompanyContract[]>(`/admin/companies/${companyId}/contracts`),

  listCallingSeats: () =>
    apiRequest<
      {
        id: string;
        recruiter_id: string;
        is_active: boolean;
        notes: string | null;
        created_at: string;
        recruiter_name: string;
        recruiter_email: string;
      }[]
    >("/admin/calling-seats"),

  grantCallingSeat: (recruiterId: string, notes?: string) =>
    apiRequest<{ id: string; recruiter_id: string; is_active: boolean }>("/admin/calling-seats", {
      method: "POST",
      body: { recruiter_id: recruiterId, notes },
    }),

  revokeCallingSeat: (recruiterId: string) =>
    apiRequest<{ message: string }>(`/admin/calling-seats/${recruiterId}`, { method: "DELETE" }),

  listInvoices: (params: { status?: string; company_id?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<AdminInvoiceListItem>>("/admin/invoices", { query: params }),

  getInvoice: (id: string) => apiRequest<AdminInvoice>(`/admin/invoices/${id}`),

  listUninvoicedPlacements: (companyId: string) =>
    apiRequest<UninvoicedPlacement[]>(`/admin/invoices/uninvoiced/${companyId}`),

  generateInvoice: (payload: {
    company_id: string;
    placement_ids: string[];
    due_date?: string;
    notes?: string;
    period_start?: string;
    period_end?: string;
  }) => apiRequest<AdminInvoice>("/admin/invoices/generate", { method: "POST", body: payload }),

  issueInvoice: (id: string) =>
    apiRequest<AdminInvoice>(`/admin/invoices/${id}/issue`, { method: "POST" }),

  approveInvoicePayment: (id: string, payload: { payment_reference: string }) =>
    apiRequest<AdminInvoice>(`/admin/invoices/${id}/approve-payment`, { method: "POST", body: payload }),

  approveInvoiceLine: (invoiceId: string, lineId: string, payload: { payment_reference: string }) =>
    apiRequest<AdminInvoice>(`/admin/invoices/${invoiceId}/lines/${lineId}/approve`, {
      method: "POST",
      body: payload,
    }),

  listPayouts: (params: { payee_type?: "recruiter" | "consultancy"; status?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<AdminPayout>>("/admin/payouts", { query: params }),

  markPayoutPaid: (id: string, payload: { payment_reference?: string; supplier_invoice_ref?: string } = {}) =>
    apiRequest<{ message: string }>(`/admin/payouts/${id}/mark-paid`, { method: "POST", body: payload }),

  bulkMarkPayoutsPaid: (payload: { payout_ids: string[]; payment_reference: string }) =>
    apiRequest<{ message: string }>("/admin/payouts/bulk-mark-paid", { method: "POST", body: payload }),

  setSupplierInvoice: (payoutId: string, payload: { supplier_invoice_ref: string; supplier_invoice_file_key?: string }) =>
    apiRequest<{ message: string }>(`/admin/payouts/${payoutId}/supplier-invoice`, {
      method: "POST",
      body: payload,
    }),
};

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";

async function contractUploadRequest(
  path: string,
  payload: {
    name?: string;
    fee_basis: "flat" | "pct_ctc";
    fee_flat_paise?: number;
    fee_pct_ctc?: number;
    guarantee_days: number;
    currency: string;
    effective_from: string;
    effective_to?: string;
    notes?: string;
    is_default?: boolean;
    activate_now?: boolean;
    pdf_file?: File;
  },
  method: "POST" | "PUT",
): Promise<CompanyContract> {
  const token = getToken();
  const body = new FormData();
  body.set("name", payload.name?.trim() || "Standard");
  body.set("fee_basis", payload.fee_basis);
  if (payload.fee_flat_paise !== undefined) body.set("fee_flat_paise", String(payload.fee_flat_paise));
  if (payload.fee_pct_ctc !== undefined) body.set("fee_pct_ctc", String(payload.fee_pct_ctc));
  body.set("guarantee_days", String(payload.guarantee_days));
  body.set("currency", payload.currency);
  body.set("effective_from", payload.effective_from);
  if (payload.effective_to) body.set("effective_to", payload.effective_to);
  if (payload.notes) body.set("notes", payload.notes);
  body.set("is_default", String(payload.is_default ?? false));
  body.set("activate_now", String(payload.activate_now ?? true));
  if (payload.pdf_file) body.set("pdf_file", payload.pdf_file);

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body,
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data?.error?.message || "Contract request failed");
  }
  return response.json();
}

/** Generic auth'd file download — fetches, resolves filename from Content-Disposition, saves via blob. */
async function downloadFile(path: string, fallbackFilename: string): Promise<void> {
  const token = getToken();
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => undefined);
    throw new Error(data?.error?.message || "Download failed");
  }
  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition") || "";
  const match = /filename="?([^"]+)"?/.exec(contentDisposition);
  const filename = match?.[1] || fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function downloadCandidateJd(
  jobId: string,
  payload: { include_recruiter_phone: boolean; include_recruiter_email: boolean },
): Promise<void> {
  const token = getToken();
  const response = await fetch(`${BASE_URL}/jobs/${jobId}/candidate-jd`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ ...payload, format: "pdf" }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => undefined);
    throw new Error(data?.error?.message || "Could not create the candidate JD");
  }
  const blob = await response.blob();
  const filename = /filename="?([^"]+)"?/.exec(response.headers.get("content-disposition") || "")?.[1] || "candidate-jd.pdf";
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function downloadContract(path: string): Promise<void> {
  return downloadFile(path, "contract.pdf");
}

/** Generic auth'd multipart upload — arbitrary form fields + one file, parsed JSON response. */
async function uploadFileRequest<T>(
  path: string,
  fields: Record<string, string | number | boolean | undefined>,
  file: File,
  fileFieldName = "file",
): Promise<T> {
  const token = getToken();
  const body = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) body.set(key, String(value));
  }
  body.set(fileFieldName, file);
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body,
    signal: AbortSignal.timeout(180_000),
  });
  const text = await response.text();
  let data: T | { error?: { message?: string } } | undefined;
  try {
    data = text ? (JSON.parse(text) as T) : undefined;
  } catch {
    throw new Error(
      response.ok
        ? "Upload returned a non-JSON response"
        : `Upload failed (${response.status}). Check the API terminal for [explore-import] lines.`,
    );
  }
  if (!response.ok) {
    const message =
      data && typeof data === "object" && "error" in data
        ? (data as { error?: { message?: string } }).error?.message
        : undefined;
    throw new Error(message || `Upload failed (${response.status})`);
  }
  return data as T;
}

export const contractsApi = {
  listCompanyContracts: (companyId: string) => apiRequest<CompanyContract[]>(`/admin/companies/${companyId}/contracts`),

  createCompanyContract: (
    companyId: string,
    payload: {
      name?: string;
      fee_basis: "flat" | "pct_ctc";
      fee_flat_paise?: number;
      fee_pct_ctc?: number;
      guarantee_days: number;
      currency: string;
      effective_from: string;
      effective_to?: string;
      notes?: string;
      is_default?: boolean;
      activate_now?: boolean;
      pdf_file?: File;
    },
  ) => contractUploadRequest(`/admin/companies/${companyId}/contracts`, payload, "POST"),

  updateContract: (
    contractId: string,
    payload: {
      name?: string;
      fee_basis: "flat" | "pct_ctc";
      fee_flat_paise?: number;
      fee_pct_ctc?: number;
      guarantee_days: number;
      currency: string;
      effective_from: string;
      effective_to?: string;
      notes?: string;
      is_default?: boolean;
      activate_now?: boolean;
      pdf_file?: File;
    },
  ) => contractUploadRequest(`/admin/contracts/${contractId}`, payload, "PUT"),

  activateContract: (contractId: string) =>
    apiRequest<CompanyContract>(`/admin/contracts/${contractId}/activate`, { method: "POST", body: { status: "active" } }),

  archiveContract: (contractId: string) =>
    apiRequest<{ message: string }>(`/admin/contracts/${contractId}`, { method: "DELETE" }),

  getHrContract: () => apiRequest<ContractSummary>("/hr/contract"),

  listHrContracts: () => apiRequest<ContractSummary[]>("/hr/contracts"),

  downloadHrContractPdf: (contractId?: string) =>
    downloadContract(contractId ? `/hr/contract/pdf?contract_id=${contractId}` : "/hr/contract/pdf"),

  downloadContractPdf: (contractId: string) => downloadContract(`/contracts/${contractId}/pdf`),

  downloadContractDraftPdf: (contractId: string) => downloadContract(`/admin/contracts/${contractId}/draft-pdf`),

  downloadHrContractDraftPdf: (contractId?: string) =>
    downloadContract(contractId ? `/hr/contract/draft-pdf?contract_id=${contractId}` : "/hr/contract/draft-pdf"),

  uploadSignedContractPdf: (contractId: string, pdfFile: File) => {
    const token = getToken();
    const body = new FormData();
    body.set("pdf_file", pdfFile);
    return fetch(`${BASE_URL}/admin/contracts/${contractId}/signed-pdf`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body,
    }).then(async (response) => {
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.error?.message || "Signed PDF upload failed");
      }
      return response.json() as Promise<CompanyContract>;
    });
  },
};

export const emailCampaignsApi = {
  listTemplates: () => apiRequest<EmailCampaignTemplate[]>("/admin/email-campaigns/templates"),

  downloadSampleCsv: (templateKey: string) =>
    downloadFile(`/admin/email-campaigns/templates/${templateKey}/sample-csv`, `${templateKey}_template.csv`),

  send: (templateKey: string, file: File, forceResend = false) =>
    uploadFileRequest<EmailCampaign>(
      "/admin/email-campaigns/send",
      { template_key: templateKey, force_resend: forceResend },
      file,
    ),

  list: (params: { page?: number; page_size?: number } = {}) =>
    apiRequest<Page<EmailCampaign>>("/admin/email-campaigns", { query: params }),

  get: (id: string) => apiRequest<EmailCampaignDetail>(`/admin/email-campaigns/${id}`),
};

export const profilesApi = {
  getUserProfile: (userId: string) => apiRequest<UserProfile>(`/profiles/users/${userId}`),
  getUserTimeline: (userId: string, params: { page?: number; page_size?: number } = {}) =>
    apiRequest<Page<TimelineEntry>>(`/profiles/users/${userId}/timeline`, { query: params }),
  getCandidateProfile: (candidateId: string) =>
    apiRequest<CandidateProfile>(`/profiles/candidates/${candidateId}`),
  getCandidateTimeline: (candidateId: string, params: { page?: number; page_size?: number } = {}) =>
    apiRequest<Page<TimelineEntry>>(`/profiles/candidates/${candidateId}/timeline`, { query: params }),
};

export const disputesApi = {
  list: (params: { status?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Dispute>>("/disputes", { query: params }),

  get: (id: string) => apiRequest<Dispute>(`/disputes/${id}`),

  create: (payload: {
    dispute_type: DisputeType;
    description: string;
    submission_id?: string;
    placement_id?: string;
  }) => apiRequest<Dispute>("/disputes", { method: "POST", body: payload }),
};

export const jobsApi = {
  list: (params: { q?: string; segment?: Segment; city?: string; work_mode?: string; remote?: boolean; status?: JobStatus; sort?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Job>>("/jobs", { query: params }),

  availableCities: (params: { segment?: Segment; work_mode?: string } = {}) =>
    apiRequest<string[]>("/jobs/available-cities", { query: params }),

  get: (id: string) => apiRequest<JobDetail>(`/jobs/${id}`),

  create: (payload: JobCreatePayload) => apiRequest<Job>("/jobs", { method: "POST", body: payload }),

  update: (id: string, payload: Partial<JobCreatePayload>) =>
    apiRequest<Job>(`/jobs/${id}`, { method: "PATCH", body: payload }),

  publish: (id: string) => apiRequest<Job>(`/jobs/${id}/publish`, { method: "POST" }),

  pause: (id: string) => apiRequest<Job>(`/jobs/${id}/pause`, { method: "POST" }),

  close: (id: string) => apiRequest<Job>(`/jobs/${id}/close`, { method: "POST" }),

  parseDocument: (payload: { text: string }) =>
    apiRequest<JobParseDocumentResponse>("/jobs/parse-document", {
      method: "POST",
      body: payload,
    }),

  downloadCandidateJd,

  candidateJdWhatsApp: (
    id: string,
    payload: { include_recruiter_phone: boolean; include_recruiter_email: boolean },
  ) =>
    apiRequest<{ text: string; intake_url: string }>(`/jobs/${id}/candidate-jd`, {
      method: "POST",
      body: { ...payload, format: "whatsapp" },
    }),

  insights: (id: string) => apiRequest<JobInsightPublic[]>(`/jobs/${id}/insights`),

  poolMatches: (id: string) => apiRequest<PoolCandidateMatch[]>(`/jobs/${id}/pool-matches`),
};

export const hiringFlowsApi = {
  listTemplates: (companyId: string) =>
    apiRequest<FlowTemplate[]>(`/companies/${companyId}/hiring-flows`),

  createTemplate: (
    companyId: string,
    payload: { name: string; description?: string; stages: StageInput[] },
  ) =>
    apiRequest<FlowTemplate>(`/companies/${companyId}/hiring-flows`, {
      method: "POST",
      body: payload,
    }),

  deleteTemplate: (companyId: string, templateId: string) =>
    apiRequest<void>(`/companies/${companyId}/hiring-flows/${templateId}`, {
      method: "DELETE",
    }),

  setJobStages: (jobId: string, stages: StageInput[]) =>
    apiRequest<Stage[]>(`/jobs/${jobId}/stages`, {
      method: "PUT",
      body: { stages },
    }),

  applyTemplate: (jobId: string, templateId: string) =>
    apiRequest<Stage[]>(`/jobs/${jobId}/apply-template/${templateId}`, {
      method: "POST",
    }),
};

export const candidatesApi = {
  list: (params: {
    q?: string;
    page?: number;
    page_size?: number;
    sort?: string;
    match_tier?: string;
    contact?: string;
  } = {}) => apiRequest<Page<Candidate>>("/candidates", { query: params }),

  get: (id: string) => apiRequest<Candidate>(`/candidates/${id}`),

  create: (
    payload: Partial<Candidate> & {
      full_name: string;
      phone: string;
      consent_given: boolean;
      resume_id?: string;
    },
  ) => apiRequest<Candidate>("/candidates", { method: "POST", body: payload }),

  update: (id: string, payload: Partial<Candidate>) =>
    apiRequest<Candidate>(`/candidates/${id}`, { method: "PATCH", body: payload }),

  uploadResume: async (file: File, phone?: string): Promise<IntakeResumeUploadResponse> => {
    const body = new FormData();
    body.set("file", file);
    if (phone) body.set("phone", phone);
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${BASE_URL}/candidates/resume`, {
      method: "POST",
      headers,
      body,
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;
    if (!response.ok) {
      const message = data?.error?.message || "Resume upload failed";
      throw new ApiError(response.status, data?.error?.code ?? "upload_failed", message);
    }
    return data as IntakeResumeUploadResponse;
  },

  parseResume: (payload: { text: string; phone?: string; resume_id?: string }) =>
    apiRequest<IntakeResumeParseResponse>("/candidates/resume/parse", {
      method: "POST",
      body: payload,
    }),

  topMatches: (id: string, limit = TOP_MATCH_LIMIT) =>
    apiRequest<TopJobMatch[]>(`/candidates/${id}/top-matches`, { query: { limit } }),

  matches: (id: string) => apiRequest<TopJobMatch[]>(`/candidates/${id}/matches`),

  logContact: (
    id: string,
    payload: { job_id?: string | null; channel: string; outcome: string; note?: string },
  ) => apiRequest<ContactLog>(`/candidates/${id}/contact-log`, { method: "POST", body: payload }),

  interviewPrep: (id: string, payload: { job_id: string }) =>
    apiRequest<InterviewPrep>(`/candidates/${id}/interview-prep`, {
      method: "POST",
      body: payload,
    }),
};

export const submissionsApi = {
  list: (params: { job_id?: string; status?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Submission>>("/submissions", { query: params }),

  get: (id: string) => apiRequest<SubmissionDetail>(`/submissions/${id}`),

  create: (payload: {
    job_id: string;
    candidate_id: string;
    pitch_note?: string;
    attestation: SubmissionAttestation;
  }) =>
    apiRequest<Submission>("/submissions", { method: "POST", body: payload }),

  moveStage: (
    id: string,
    payload: { stage_id: string; notes?: string; mark_as?: "passed" | "skipped" },
  ) => apiRequest<Submission>(`/submissions/${id}/move-stage`, { method: "POST", body: payload }),

  submitForReview: (id: string, payload: { notes?: string } = {}) =>
    apiRequest<Submission>(`/submissions/${id}/submit-for-review`, { method: "POST", body: payload }),

  confirmHire: (
    id: string,
    payload: { joining_date: string; offered_ctc_paise?: number; notes?: string },
  ) => apiRequest<Submission>(`/submissions/${id}/confirm-hire`, { method: "POST", body: payload }),

  reject: (id: string, payload: { reason: string; notes?: string }) =>
    apiRequest<Submission>(`/submissions/${id}/reject`, { method: "POST", body: payload }),

  withdraw: (id: string) =>
    apiRequest<Submission>(`/submissions/${id}/withdraw`, { method: "POST" }),

  rationale: (id: string) =>
    apiRequest<{ rationale: string | null; cached: boolean }>(`/submissions/${id}/rationale`, {
      method: "POST",
    }),
};

export const earningsApi = {
  summary: () => apiRequest<EarningsSummary>("/users/me/earnings"),

  payouts: (params: { status?: "pending" | "received" | "all"; submission_id?: string; page?: number; page_size?: number } = {}) =>
    apiRequest<Page<Payout>>("/users/me/payouts", { query: params }),
};

export const intakeApi = {
  createLink: () => apiRequest<IntakeLink>("/users/me/intake-link", { method: "POST" }),

  listLinks: () => apiRequest<IntakeLink[]>("/users/me/intake-links"),

  deactivate: (id: string) =>
    apiRequest<IntakeLink>(`/users/intake-links/${id}/deactivate`, { method: "POST" }),
};

export type JobIntakeContext = { job: string; sig: string };

export const publicIntakeApi = {
  get: (token: string, context?: JobIntakeContext) =>
    apiRequest<IntakePublicInfo>(`/public/intake/${token}`, { auth: false, query: context }),

  requestPhoneOtp: (token: string, phone: string, context?: JobIntakeContext) =>
    apiRequest<IntakePhoneOtpResponse>(`/public/intake/${token}/phone/request-otp`, {
      method: "POST",
      body: { phone },
      auth: false,
      query: context,
    }),

  verifyPhoneOtp: (token: string, phone: string, code: string, context?: JobIntakeContext) =>
    apiRequest<IntakePhoneVerifiedResponse>(`/public/intake/${token}/phone/verify-otp`, {
      method: "POST",
      body: { phone, code },
      auth: false,
      query: context,
    }),

  uploadResume: async (
    token: string,
    phone: string,
    phoneVerificationToken: string,
    file: File,
    context?: JobIntakeContext,
  ): Promise<IntakeResumeUploadResponse> => {
    const body = new FormData();
    body.set("phone", phone);
    body.set("phone_verification_token", phoneVerificationToken);
    body.set("file", file);
    const query = context ? `?${new URLSearchParams(context).toString()}` : "";
    const response = await fetch(`${BASE_URL}/public/intake/${token}/resume${query}`, {
      method: "POST",
      body,
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;
    if (!response.ok) {
      const message = data?.error?.message || "Resume upload failed";
      throw new ApiError(response.status, data?.error?.code ?? "upload_failed", message);
    }
    return data as IntakeResumeUploadResponse;
  },

  parseResume: (
    token: string,
    payload: {
      phone: string;
      phone_verification_token: string;
      text: string;
      resume_id?: string;
    },
    context?: JobIntakeContext,
  ) =>
    apiRequest<IntakeResumeParseResponse>(`/public/intake/${token}/resume/parse`, {
      method: "POST",
      body: payload,
      auth: false,
      query: context,
    }),

  submit: (token: string, payload: IntakeSubmitPayload, context?: JobIntakeContext) =>
    apiRequest<IntakeSubmitResponse>(`/public/intake/${token}/submit`, {
      method: "POST",
      body: payload,
      auth: false,
      query: context,
    }),
};

export const publicSystemApplyApi = {
  get: () => apiRequest<IntakePublicInfo>("/public/apply", { auth: false }),

  requestPhoneOtp: (phone: string) =>
    apiRequest<IntakePhoneOtpResponse>("/public/apply/phone/request-otp", {
      method: "POST",
      body: { phone },
      auth: false,
    }),

  verifyPhoneOtp: (phone: string, code: string) =>
    apiRequest<IntakePhoneVerifiedResponse>("/public/apply/phone/verify-otp", {
      method: "POST",
      body: { phone, code },
      auth: false,
    }),

  uploadResume: async (
    file: File,
    phone?: string,
    phoneVerificationToken?: string,
  ): Promise<IntakeResumeUploadResponse> => {
    const body = new FormData();
    body.set("file", file);
    if (phone) body.set("phone", phone);
    if (phoneVerificationToken) body.set("phone_verification_token", phoneVerificationToken);
    const response = await fetch(`${BASE_URL}/public/apply/resume`, {
      method: "POST",
      body,
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;
    if (!response.ok) {
      const message = data?.error?.message || "Resume upload failed";
      throw new ApiError(response.status, data?.error?.code ?? "upload_failed", message);
    }
    return data as IntakeResumeUploadResponse;
  },

  parseResume: (payload: {
    phone?: string;
    phone_verification_token?: string;
    text: string;
    resume_id?: string;
  }) =>
    apiRequest<IntakeResumeParseResponse>("/public/apply/resume/parse", {
      method: "POST",
      body: payload,
      auth: false,
    }),

  submit: (payload: IntakeSubmitPayload) =>
    apiRequest<IntakeSubmitResponse>("/public/apply", {
      method: "POST",
      body: payload,
      auth: false,
    }),
};

export const segmentsApi = {
  publicCatalog: () =>
    apiRequest<SegmentCatalog>("/public/segments", { auth: false }),

  catalog: () => apiRequest<SegmentCatalog>("/segments"),
};

export const roleFamiliesApi = {
  publicCatalog: () =>
    apiRequest<RoleFamilyCatalog>("/public/role-families", { auth: false }),

  catalog: () => apiRequest<RoleFamilyCatalog>("/role-families"),
};

export const skillsApi = {
  publicCatalog: () =>
    apiRequest<SkillCatalog>("/public/skills", { auth: false }),

  catalog: () => apiRequest<SkillCatalog>("/skills"),
};

export const citiesApi = {
  publicCatalog: () =>
    apiRequest<CityCatalog>("/public/cities", { auth: false }),

  catalog: () => apiRequest<CityCatalog>("/cities"),
};

export const dashboardApi = {
  catalog: () => apiRequest<DashboardCatalog>("/dashboard/reports"),

  runReport: (reportKey: string) =>
    apiRequest<DashboardReportRun>(`/dashboard/reports/${reportKey}/run`, { method: "POST" }),

  saveLayout: (layout: DashboardLayout) =>
    apiRequest<DashboardCatalog>("/dashboard/layout", { method: "PUT", body: { layout } }),
};

export type QueueMatch = {
  id: string;
  candidate_id: string;
  job_id: string;
  status: string;
  assigned_recruiter_id: string | null;
  lock_expires_at: string | null;
  cooldown_until: string | null;
  follow_up_expires_at: string | null;
  attempt_count: number;
  last_attempt_at: string | null;
  last_disposition: string | null;
  reject_reason: string | null;
  cached_score: number | null;
  inbound_for_job: boolean;
  score_breakdown: {
    skill?: number;
    experience?: number;
    freshness?: number;
    engagement?: number;
    ctc_fit?: number;
    location_fit?: number;
    availability_fit?: number;
    contact_penalty?: number;
    vector_cosine?: number;
    vector_fit?: number;
  } | null;
  submission_id: string | null;
  created_at: string;
  updated_at: string;
  callable_profile?: boolean;
  candidate: {
    id: string;
    full_name: string;
    phone: string | null;
    email: string | null;
    cities: string[];
    skills: string[];
    years_experience: number | null;
    resume_url: string | null;
    profile_updated_at: string | null;
    created_at?: string | null;
    source: string;
    ownership: string;
    current_ctc_paise?: number | null;
    expected_ctc_paise?: number | null;
    availability_in_days?: number | null;
    current_title?: string | null;
    current_company?: string | null;
    work_mode_preference?: string | null;
    willing_to_relocate?: boolean;
    employment_status?: string | null;
    education_level?: string | null;
    education_summary?: string | null;
    languages?: string[];
  } | null;
  skill_fit?: {
    matched: string[];
    missing_must: string[];
    missing_nice: string[];
    matched_must: string[];
    matched_nice: string[];
    has_all_must: boolean;
    required: { skill: string; must_have: boolean }[];
  } | null;
};

export type DispositionPayload = {
  disposition: string;
  notes?: string;
  duration_seconds?: number;
  reject_reason?: string;
  never_call_again?: boolean;
};

export type QueueBank = {
  items: QueueMatch[];
  saved_count: number;
  saved_limit: number;
  passes_today: number;
  pass_limit: number;
};

export const queueApi = {
  list: (
    jobId: string,
    limit = 1,
    mustHaveOnly = true,
    withinBudgetOnly = false,
    matchingLocationsOnly = false,
    callableOnly = true,
  ) =>
    apiRequest<QueueMatch[]>(`/jobs/${jobId}/queue`, {
      query: {
        limit,
        must_have_only: mustHaveOnly,
        within_budget_only: withinBudgetOnly,
        matching_locations_only: matchingLocationsOnly,
        callable_only: callableOnly,
      },
    }),

  cooldown: (jobId: string) => apiRequest<QueueMatch[]>(`/jobs/${jobId}/queue/cooldown`),

  saved: (jobId: string) => apiRequest<QueueBank>(`/jobs/${jobId}/queue/saved`),

  myOwned: () => apiRequest<QueueMatch[]>("/me/queue/owned"),

  claim: (matchId: string) =>
    apiRequest<QueueMatch>(`/matches/${matchId}/claim`, { method: "POST" }),

  saveForward: (matchId: string) =>
    apiRequest<QueueBank>(`/matches/${matchId}/save-forward`, { method: "POST" }),

  pass: (matchId: string, rejectReason: string) =>
    apiRequest<QueueBank>(`/matches/${matchId}/pass`, {
      method: "POST",
      body: { reject_reason: rejectReason },
    }),

  release: (matchId: string) =>
    apiRequest<QueueMatch>(`/matches/${matchId}/release`, { method: "POST" }),

  disposition: (matchId: string, payload: DispositionPayload, idempotencyKey: string) =>
    apiRequest<QueueMatch>(`/matches/${matchId}/disposition`, {
      method: "POST",
      body: payload,
      headers: { "Idempotency-Key": idempotencyKey },
    }),

  rationale: (matchId: string) =>
    apiRequest<{ match_id: string; rationale: string | null; cached: boolean }>(
      `/matches/${matchId}/rationale`,
      { method: "POST" },
    ),

  wsTicket: (jobId: string) =>
    apiRequest<{ ticket: string; expires_in_seconds: number }>(`/jobs/${jobId}/queue/ws-ticket`, {
      method: "POST",
    }),

  wsUrl: (jobId: string, ticket: string) => {
    const base = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";
    const origin = typeof window === "undefined" ? "http://localhost" : window.location.origin;
    const url = new URL(base, origin);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = `${url.pathname.replace(/\/$/, "")}/ws/jobs/${jobId}/queue`;
    url.search = `ticket=${encodeURIComponent(ticket)}`;
    return url.toString();
  },
};
